import {
  boundCheckboxField,
  BoundForm,
  boundSelectField,
  boundTextAreaField,
  boundTextField,
  Css,
  useComputed,
  useTestIds,
} from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { ReactNode } from "react";
import { ReadyPlanOptionGroupPersonalizationType } from "src/generated/graphqlTypes";
import { PlanEditorHeader } from "src/routes/components/PlanConfiguration/PlanEditorHeader";
import { EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment } from "src/routes/libraries/design-catalog/personalization-option/EditPersonalizationOptionWizard.generated";
import {
  SlotsSection,
  TargetGroupsSection,
} from "src/routes/libraries/design-catalog/personalization-option/components/SlotsSection";
import {
  canAssignTargetCombinations,
  FormValue,
} from "src/routes/libraries/design-catalog/personalization-option/utils";
import { zeroTo } from "src/utils";

type DetailsStepProps = {
  formState: ObjectState<FormValue>;
  title: string;
  personalizationTypes: { code: ReadyPlanOptionGroupPersonalizationType; name: string }[];
  /** True when editing an existing option (type cannot be changed). */
  isExisting: boolean;
  targetGroups: EditPersonalizationOptionWizard_ReadyPlanOptionGroupFragment[];
};

/**
 * The first step: option details (name/type/levels), the target groups it applies to ("Apply to"),
 * and the slots it controls.
 */
export function DetailsStep(props: DetailsStepProps) {
  const { formState, title, personalizationTypes, isExisting, targetGroups } = props;
  const tid = useTestIds({});
  const isPackage = useComputed(
    () => formState.type.value === ReadyPlanOptionGroupPersonalizationType.Package,
    [formState],
  );
  const showApplyTo = useComputed(() => canAssignTargetCombinations(formState.type.value), [formState]);

  return (
    <Observer>
      {() => (
        <div css={Css.bgWhite.fg1.oya.df.fdc.$} {...tid}>
          <PlanEditorHeader title={title} />
          <div css={Css.df.fdc.gap("48px").wPx(678).asc.pt5.pb5.$}>
            <Section title="Details">
              <BoundForm
                formState={formState}
                rows={[
                  {
                    type: boundSelectField({
                      options: personalizationTypes,
                      getOptionLabel: (o) => o.name,
                      getOptionValue: (o) => o.code,
                      disabled: isExisting ? "Type cannot be modified for existing options" : undefined,
                    }),
                  },
                  { name: boundTextField({}) },
                  { marketingDescription: boundTextAreaField({}) },
                  {
                    levels: boundSelectField({
                      options: [{ name: "", value: null }, ...zeroTo(5, (i) => ({ name: `${i + 1}`, value: i + 1 }))],
                      getOptionLabel: (v) => v.name,
                      getOptionValue: (v) => v.value,
                    }),
                  },
                  ...(isPackage
                    ? [
                        {
                          displayPackageOptionsAsSingleProduct: boundCheckboxField({
                            fullWidth: true,
                            label: "Display option as personalization card",
                            helperText: `In Checkout, this will display the option as a personalization card with multiple choices within it,
                                rather than as a standard package option with one choice.`,
                          }),
                        },
                      ]
                    : []),
                ]}
              />
            </Section>

            {showApplyTo && (
              <Section title="Apply To">
                <TargetGroupsSection formState={formState} tid={tid} targetGroups={targetGroups} />
              </Section>
            )}

            <Section title="Slots" subtitle="Slot order defines their display in Checkout.">
              <SlotsSection formState={formState} tid={tid} targetGroups={targetGroups} />
            </Section>
          </div>
        </div>
      )}
    </Observer>
  );
}

function Section(props: { title: string; subtitle?: string; children: ReactNode }) {
  const { title, subtitle, children } = props;
  return (
    <section css={Css.df.fdc.gap3.$}>
      <div css={Css.df.fdc.gap1.$}>
        <h2 css={Css.xl.gray900.$}>{title}</h2>
        {subtitle && <p css={Css.sm.gray600.$}>{subtitle}</p>}
      </div>
      {children}
    </section>
  );
}
